import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Sponsor {
  id: string;
  name: string;
  logo?: string;
  logo_img?: string;
  website?: string;
  created_at?: string;
}

const ContactSection = () => {
  const [isSponsorsOpen, setIsSponsorsOpen] = useState(false);
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('sponsors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching sponsors:', error);
        setSponsors([]);
        return;
      }

      setSponsors(data || []);
    } catch (error) {
      console.error('Unexpected error fetching sponsors:', error);
      setSponsors([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="px-4 py-6 bg-gray-900 min-h-screen mx-auto" style={{ maxWidth: '40rem' }}>
      <div className="bg-gradient-to-r from-pink-500 to-pink-600 text-white px-4 py-4 rounded-t-lg">
        <h3 className="text-lg font-bold">Información del Club</h3>
      </div>
      
      <div className="bg-gray-800 rounded-b-lg p-4 space-y-4">
        <div className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <i className="ri-phone-line text-green-600 text-xl"></i>
          </div>
          <div>
            <p className="text-sm text-gray-300">Teléfono</p>
            <a 
              href="tel:+34649521022" 
              className="text-white font-medium"
            >
              +34 649 52 10 22
            </a>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <i className="ri-mail-line text-blue-600 text-xl"></i>
          </div>
          <div>
            <p className="text-sm text-gray-300">Email</p>
            <a 
              href="mailto:viulesportbunyola@gmail.com" 
              className="text-white font-medium"
            >
              viulesportbunyola@gmail.com
            </a>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
          <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center">
            <i className="ri-instagram-line text-pink-600 text-xl"></i>
          </div>
          <div>
            <p className="text-sm text-gray-300">Instagram</p>
            <a 
              href="https://instagram.com/viubasquet_bunyola" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white font-medium"
            >
              @viubasquet_bunyola
            </a>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
            <i className="ri-time-line text-purple-600 text-xl"></i>
          </div>
          <div>
            <p className="text-sm text-gray-300">Horarios</p>
            <div className="text-white font-medium">
              <p>Lunes a Viernes: 17:00 - 22:00</p>
              <p>Sábados: 09:00 - 14:00</p>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 p-3 bg-gray-700 rounded-lg">
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
            <i className="ri-map-pin-line text-orange-600 text-xl"></i>
          </div>
          <div>
            <p className="text-sm text-gray-300">Ubicación</p>
            <a 
              href="https://maps.app.goo.gl/PF6wa3hcxDaqCV1y7"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white font-medium"
            >
              Polideportivo Municipal de Bunyola
            </a>
          </div>
        </div>
        
        {/* Sección de Patrocinadores */}
        <div className="space-y-4">
          <button
            onClick={() => setIsSponsorsOpen(!isSponsorsOpen)}
            className="w-full flex items-center space-x-4 p-3 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <i className={`ri-group-line text-purple-600 text-xl transition-transform duration-300 ${
                isSponsorsOpen ? 'rotate-180' : ''
              }`}></i>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm text-gray-300">Patrocinadores</p>
              <p className="text-white font-medium">
                {sponsors.length > 0 ? `${sponsors.length} patrocinador${sponsors.length !== 1 ? 'es' : ''}` : 'Sin patrocinadores'}
              </p>
            </div>
            <i 
              className={`ri-arrow-down-s-line text-xl text-gray-400 transition-transform duration-300 ${
                isSponsorsOpen ? 'rotate-180' : ''
              }`}
            ></i>
          </button>

          {/* Contenido del acordeón */}
          {isSponsorsOpen && (
            <div className="space-y-4 pt-2">
              {loading ? (
                <div className="text-center py-4">
                  <div className="inline-block w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                </div>
              ) : sponsors.length === 0 ? (
                <div className="text-center py-4 bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-400">No hay patrocinadores registrados</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {sponsors.map((sponsor) => {
                    const logoUrl = sponsor.logo_img || sponsor.logo;
                    return (
                      <a
                        key={sponsor.id}
                        href={sponsor.website || '#'}
                        target={sponsor.website ? "_blank" : undefined}
                        rel={sponsor.website ? "noopener noreferrer" : undefined}
                        className="bg-gray-700 rounded-lg p-3 hover:bg-gray-600 transition-colors duration-300 flex items-center justify-center min-h-[80px]"
                      >
                        {logoUrl ? (
                          <img
                            src={logoUrl}
                            alt={sponsor.name}
                            className="max-w-full max-h-12 object-contain"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                parent.innerHTML = `<div class="text-center"><p class="text-white text-xs">${sponsor.name}</p></div>`;
                              }
                            }}
                          />
                        ) : (
                          <p className="text-white text-xs text-center">{sponsor.name}</p>
                        )}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>


    </section>
  );
};

export default ContactSection;
